import type { ContentTypeCode } from '@/services/cms'

/**
 * Min distance (px) between the logo overlay button and the screen edge.
 * Used in both ViverseLogoOverlay (Tailwind `left-6`/`right-6` = 24px)
 * and LogoOverlayRecommendationPanel (BUTTON_SIDE_MARGIN) so both stay in sync.
 */
export const LOGO_OVERLAY_SCREEN_EDGE_MARGIN = 24

/** Content kind — single source of truth. Use `LogoOverlayContentKind.GAME` / `.EXPERIENCE` in all comparisons and defaults. */
export const LogoOverlayContentKind = {
  GAME: 'game',
  EXPERIENCE: 'experience',
} as const

export type LogoOverlayContentKind =
  (typeof LogoOverlayContentKind)[keyof typeof LogoOverlayContentKind]

/**
 * The resolved overlay UI model derived from a room's `content_type.code`.
 * `null` means the overlay must fail closed: skip recommendation fetch and
 * keep the page interactive without crashing or hanging.
 */
export type LogoOverlayContentModel = {
  /** The CMS `content_type.code` this model was derived from.
   *  Passed to the recommendations Route Handler so the server owns the mapping to `recomm_type`. */
  contentTypeCode: ContentTypeCode
  /** Content kind — use this for styling decisions (e.g. badge colour, thumbnail shape). */
  contentKind: LogoOverlayContentKind
  /** i18n key for the button label, derived from `content_type.code`. */
  labelKey: string
  /** i18n key for the View All CTA label, derived from `content_type.code`. */
  viewAllLabelKey: string
  /** View All CTA href. `null` hides the CTA entirely. */
  viewAllHref: string | null
  /** i18n key for the dynamic wordmark label (e.g. 'More worlds').
   *  Resolved to a plain string at the call site via `t(overlayModel.wordmarkLabel)`. */
  wordmarkLabel: string
}

/** Which content type codes have the overlay, and which kind they map to. */
const CODE_TO_KIND: Partial<Record<ContentTypeCode, LogoOverlayContentKind>> = {
  games: LogoOverlayContentKind.GAME,
  experiences: LogoOverlayContentKind.EXPERIENCE,
  templates: LogoOverlayContentKind.EXPERIENCE,
}

/** UI config keyed by content kind. `contentKind` itself is not stored — it equals the key. */
const KIND_CONFIG: Record<
  LogoOverlayContentKind,
  Omit<LogoOverlayContentModel, 'contentTypeCode' | 'contentKind'>
> = {
  [LogoOverlayContentKind.GAME]: {
    labelKey: 'logo-overlay.button.label.games',
    viewAllLabelKey: 'logo-overlay.view-all.games',
    viewAllHref: '/games',
    wordmarkLabel: 'logo-overlay.wordmark.more-games',
  },
  [LogoOverlayContentKind.EXPERIENCE]: {
    labelKey: 'logo-overlay.button.label.experiences',
    viewAllLabelKey: 'logo-overlay.view-all.experiences',
    viewAllHref: '/?type=experience#explore-viverse',
    wordmarkLabel: 'logo-overlay.wordmark.more-experiences',
  },
}

/**
 * Maps a CMS v3 `content_type.code` to the overlay UI model.
 *
 * Returns `null` for `videos`, missing, or any unsupported value so callers
 * can fail closed without issuing a recommendation fetch or crashing.
 */
export const getLogoOverlayContentModel = (
  code: ContentTypeCode | null | undefined,
): LogoOverlayContentModel | null => {
  if (!code) return null
  const kind = CODE_TO_KIND[code]
  if (!kind) return null
  return { ...KIND_CONFIG[kind], contentTypeCode: code, contentKind: kind }
}
